'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DEFAULT_LANGUAGE,
  FALLBACK_CHAIN,
  LANGUAGE_STORAGE_KEY,
  Language,
  translations,
} from '@/lib/i18n/translations'

export interface I18nContextValue {
  language: Language
  setLanguage: (language: Language) => void
  t: (key: string, variables?: Record<string, string | number>) => string
  hydrated: boolean
}

export const I18nContext = createContext<I18nContextValue | null>(null)

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const storedLanguage =
      typeof window !== 'undefined'
        ? (window.localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null)
        : null

    const detected =
      typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('zh')
        ? 'zh'
        : DEFAULT_LANGUAGE

    if (storedLanguage && translations[storedLanguage]) {
      setLanguage(storedLanguage)
    } else {
      setLanguage(detected)
    }

    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated) return

    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    const langAttribute = language === 'zh' ? 'zh-CN' : 'en'
    document.documentElement.lang = langAttribute
  }, [hydrated, language])

  const translate = useCallback(
    (key: string, variables?: Record<string, string | number>) => {
      // Fallback chain: requested language -> English (canonical) -> Chinese -> key
      let translation: string = key
      const chain = [language, ...FALLBACK_CHAIN.filter(l => l !== language)]
      for (const lang of chain) {
        const value = translations[lang]?.[key]
        if (value !== undefined) {
          translation = value
          break
        }
      }
      if (variables) {
        Object.entries(variables).forEach(([varName, value]) => {
          translation = translation.replace(new RegExp(`\\{${varName}\\}`, 'g'), String(value))
        })
      }
      return translation
    },
    [language]
  )

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: translate,
      hydrated,
    }),
    [language, translate, hydrated]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
